import {formatTime} from "btdt/shared";
import {areaPin, locationPin, userPin} from "btdt/assets";
import {mapId} from "btdt/config";

class Map {
    app = null
    el = null
    map = null
    infoWindow = null
    userMarker = null
    locationSvg = null
    userSvg = null
    areaSvg = null
    searchQuery = null
    countryFilter = null

    constructor(el, app) {
        this.app = app
        this.el = el

        this.countryFilter = this.el.dataset.countryFilter

        this.locationSvg = this.loadSvg(locationPin)
        this.userSvg = this.loadSvg(userPin)
        this.areaSvg = this.loadSvg(areaPin)

        this.initMap();
        this.initInfoWindow();
        this.fitMapToLocations();
        this.addLocationMarkers();

        this.sortLocationsByName();

        this.app.watchState('clickedSearchLocationElement', location => {
            if (!location) {
                return
            }

            const foundIndex = this.app.locations.findIndex(l => l.hash === location.hash)

            if (foundIndex === -1) {
                return
            }

            const found = this.app.locations[foundIndex]

            this.openInfoWindow(found.marker, found)
            this.app.setState('clickedMapLocation', found)

            this.fitMapToLocations([found])
        })


        // navigator.geolocation.getCurrentPosition((position) => {
        //     this.setUserLocation(position.coords.latitude, position.coords.longitude)
        // });
    }

    initMap() {
        this.map = new this.app.google.maps.Map(this.el, {
            center: {lat: 51, lng: -109}, zoom: 3, mapId: mapId
        });
    }

    initInfoWindow() {
        this.infoWindow = new this.app.google.maps.InfoWindow()
    }

    /**
     * Re-centers the map to fit the provided locations
     */
    fitMapToLocations(locations) {
        if (!this.map) {
            return;
        }

        if (locations === undefined) {
            locations = this.app.locations.filter(l => !l.service_area)
            if (this.countryFilter) {
                locations = locations.filter(l => l.country === this.countryFilter)
            }
        }

        if (locations.length === 1) {
            this.map.setZoom(10)
            this.map.setCenter(new this.app.google.maps.LatLng(locations[0].lat, locations[0].lng))
        } else {
            const bounds = new this.app.google.maps.LatLngBounds();
            locations.forEach(l => bounds.extend(new this.app.google.maps.LatLng(l.lat, l.lng)))
            this.map.fitBounds(bounds, 32);
        }
    }

    /**
     * Resets the entire component to it's initial state
     */
    reset() {
        this.initMap();
        this.fitMapToLocations();
        if (this.userMarker) {
            this.removeMarker(this.userMarker)
        }
        this.addLocationMarkers()
    }

    /**
     * Adds markers to the map for each location
     */
    addLocationMarkers() {

        let filtered = this.app.locations.filter(l => !l.service_area)

        if (this.countryFilter) {
            filtered = filtered.filter(l => l.country === this.countryFilter)
        }

        filtered.forEach(location => {
            const pin = location.service_area ? this.areaSvg : this.locationSvg
            this.createMarker(location, pin)
        })

        // hide or show markers based on zoom level
        // disabled because it doesn't seem to work consistently when changing the map zoom programmatically

        // this.map.addListener('zoom_changed', () => {
        //     const zoom = this.map.getZoom()
        //     if (zoom ){
        //         this.app.locations.forEach(location => {
        //             if (location.service_area) {
        //                 location.marker.map = zoom > 8 ? this.map : null;
        //             }
        //         })
        //     }
        // })

    }

    /**
     * Creates a Google Maps marker for a given location and adds it to the map
     */
    createMarker(location, pinSvg) {

        if (!this.map) {
            return;
        }

        // use the supplied pin SVG or fall back to default
        const pinElement = pinSvg ? pinSvg.cloneNode(true) : new this.app.google.maps.marker.PinElement().element

        // set service area location pins to hide if they overlap with primary locations

        // const collisionBehaviour = location.service_area
        //     ? this.app.google.maps.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY
        //     : this.app.google.maps.CollisionBehavior.REQUIRED_AND_HIDES_OPTIONAL

        const marker = new this.app.google.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: {lat: parseFloat(location.lat), lng: parseFloat(location.lng)},
            title: location.name,
            content: pinElement,
            gmpClickable: true, // collisionBehavior: collisionBehaviour
        });

        marker.addListener("click", () => {
            this.openInfoWindow(marker, location)
            this.app.setState('clickedMapLocation', location)
        });

        location.marker = marker

        // draw lines between service areas and primary location
        // disabled because they didn't want it

        // if (location.service_area) {
        //     const line = new this.app.google.maps.Polyline({
        //         path: [
        //             {lat: parseFloat(location.lat), lng: parseFloat(location.lng)},
        //             {lat: parseFloat(location.parent_lat), lng:parseFloat(location.parent_lng)}
        //         ],
        //         geodesic: true,
        //         strokeColor: "#246eb9",
        //         strokeOpacity: 1,
        //         strokeWeight: 2,
        //     });
        //
        //     line.setMap(this.map);
        // }

        return marker
    }

    /**
     * Opens the info window for a given pin marker
     */
    openInfoWindow(marker, location) {
        let content = '';
        content = this.locationMarkerContent(location)

        this.infoWindow.close();
        this.infoWindow.setContent(`
                <div class="px-2 pb-1">
                    <a href= "${location.site}" class="heading-h1 block text-brand-blue mb-4 !decoration-transparent hover:!decoration-brand-green">
                        ${location.name}
                    </a>
                    <div class="small-p">
                        ${content}
                    </div>
                </div>
            `);
        this.infoWindow.open(marker.map, marker);
    }

    /**
     * Removes a marker from the map
     */
    removeMarker(marker) {
        marker.map = null
    }

    /**
     * Builds HTML to display a location's information to be used in a map marker
     */
    locationMarkerContent(location) {

        let addressStr = ''

        if (location.service_area && location.parent) {
            addressStr += `<p class="italic">*Served by our ${location.parent.address.city} location</p>`
        }

        if (location.address?.street && location.address?.city && location.address?.region && location.address?.postal_code) {
            addressStr += `<p>
                ${location.address.street}<br>
                ${location.address.city}, ${location.address.region.label}, ${location.address.postal_code}
            </p>`
        }

        let contactStr = ''
        let contactLinks = []

        if (location.email) {
            contactLinks.push(`<strong>Email:</strong> <a href="mailto:${location.email}">${location.email}</a>`)
        }
        if (location.phone) {
            contactLinks.push(`<strong>Phone:</strong> <a href="tel:${location.phone}">${location.phone}</a>`)
        }
        if (contactLinks.length > 0) {
            contactStr = `<p>${contactLinks.join('<br>')}</p>`
        }

        let hrStr = ''
        let hrs = []
        location.hours?.days?.forEach(day => {
            // skip if no hrs of operation are entered
            if (day.open && (!day.open_time || !day.close_time)) {
                return
            }
            let hr = `<strong>${day.day}</strong>`
            if (day.open && day.open_time && day.close_time) {
                if (day.open_time === day.close_time) {
                    hr += `Open 24 hours`
                } else {
                    hr += `${formatTime(day.open_time)}-${formatTime(day.close_time)}`
                }

            } else {
                hr += 'Closed'
            }
            hrs.push(hr)
        })

        if (hrs.length > 0) {
            hrStr = `<div class="grid grid-cols-[auto_minmax(0,_1fr)] gap-x-4">${hrs.join('')}</div>`
        }

        return `${addressStr}${contactStr}${hrStr}`
    }

    /**
     * Sorts all locations by name
     */
    sortLocationsByName() {
        this.app.locations.forEach(l => {
            l.distance = null;
        })

        this.app.locations.sort((a, b) => {

            const nameA = a.name.toUpperCase(); // ignore upper and lowercase
            const nameB = b.name.toUpperCase(); // ignore upper and lowercase

            if (nameA < nameB) {
                return -1;
            }

            if (nameA > nameB) {
                return 1;
            }

            return 0;
        });
    }

    loadSvg(svgStr) {
        const parser = new DOMParser();
        return parser.parseFromString(svgStr, "image/svg+xml",).documentElement;
    }

}

export default Map